/**
 * PORTO — porto industrial do Báltico, inverno, meio da tarde sob nuvem baixa.
 *
 * A leitura do mapa em uma frase: uma bacia de água escura meio congelada a
 * OESTE, o cais com os guindastes na borda dela, um labirinto de contêineres
 * ocupando o miolo, o galpão de carga a NORDESTE (o ponto alto disputado) e o
 * prédio da administração a SUDESTE. Trilhos entram pelo muro norte e cortam o
 * pátio até um para-choque no meio.
 *
 * Convenção: +X leste, +Z sul, Y para cima. Uma unidade = um metro.
 * O contrato completo está no topo de src/world/MapKit.js.
 *
 * ------------------------------------------------------------------------
 * DUAS RESTRIÇÕES DO MOTOR QUE MOLDARAM ESTE MAPA
 * ------------------------------------------------------------------------
 * 1. `Player._move` tem um piso implícito em y=0 ("se o pé for abaixo de zero e
 *    não houver colisor, o pé volta para zero"). Não existe cair no mundo, e
 *    também não existe cota negativa: um cais com 1,5 m de bordo livre deixaria
 *    o jogador flutuando sobre a água se ele pulasse o muro. Por isso a bacia é
 *    água CONGELADA rente ao y=0: quem pula o muro do cais cai no gelo, que é
 *    exatamente o flanco aberto e perigoso que o mapa quer ter. A ficção
 *    resolve a limitação em vez de brigar com ela.
 * 2. Mantle sobe até 1,9 m acima do pé, mas contêiner tem 2,6 m. Subir para o
 *    segundo nível é sempre por escada de aço ou passarela — nunca por pulo de
 *    fé. As rotas verticais estão desenhadas, não sorteadas.
 *
 * ------------------------------------------------------------------------
 * ROTA ALTA — o que o cabeçalho promete e o que existe (conserto de 15/08)
 * ------------------------------------------------------------------------
 * "cais -> plataforma -> passarela -> topo das pilhas" era meia verdade: a rede
 * de passarelas ligava o guindaste NORTE ao galpão por cima de quatro pilhas, e
 * as outras dezoito ficavam ilhadas — 271 m² de topo de contêiner medidos por
 * `tools/test-rota-alta.mjs` que ninguém alcançava a pé. Agora há DUAS entradas
 * (as duas escadas de guindaste) e duas redes: a antiga, ao norte, e a do sul,
 * que sai da plataforma do guindaste de z=10 e atravessa o labirinto por cima.
 * Os lances novos são pranchas de serviço: 1,2..1,6 m, sem guarda-corpo, e
 * cada um encosta na FACE de duas pilhas (não no vão entre elas, que foi o
 * defeito antigo). Sem guarda-corpo de propósito — corrimão é colisor, e nesta
 * largura ele fecharia a chegada em vez de proteger a queda.
 */

// Cotas que aparecem em mais de um lugar. Mudar aqui muda o mapa inteiro junto,
// que é o que impede passarela terminar meio metro acima do telhado.
const AGUA = 0.014;        // lâmina d'água/gelo, colada no piso implícito y=0
const CAIS_X = -25.2;      // face do muro do cais; a oeste dele é a bacia
const NIVEL_ALTO = 5.3;    // cota da rede de passarelas e do topo das pilhas
const GALPAO = { W: 12.4, E: 30.8, N: -22.4, S: -4.0, H: 7.2, TELHADO: 7.6 };

// Paleta de contêiner. Multiplica o albedo de 'rust' (avermelhado), então azul
// e verde precisam de fatores altos no canal certo — os números não são bonitos
// mas o resultado é: seis cascos que leem como seis empresas diferentes.
const CORES_CONTEINER = [
  [1.15, 0.92, 0.78],   // vermelho óxido
  [0.62, 1.35, 2.45],   // azul marítimo
  [0.55, 1.45, 0.95],   // verde
  [0.95, 1.55, 1.85],   // cinza claro
  [1.35, 1.15, 0.72],   // laranja desbotado
  [0.72, 1.05, 1.35],   // azul acinzentado
];

export default {
  id: 'porto',
  nome: 'Porto',
  regiao: 'Terminal báltico',
  seed: 20260814,

  bounds: { min: [-34, -1, -34], max: [34, 22, 34] },

  // Frente é -Z: nasce no portão sul, de costas para a cidade, olhando o pátio.
  player: { spawn: [1.5, 1.68, 29.0], yaw: 0 },

  // [x, yPé, z, yaw, kind, grounded]
  //
  // Todo ponto daqui é conferido pelo Level contra a lista de colisores e
  // DESCARTADO em silêncio (com aviso no console) se cair dentro de sólido.
  // Seis destes já nasceram assim — cinco dentro de contêiner ou de parapeito de
  // sacaria e um flutuando dois metros ao lado do topo da pilha que devia
  // ocupar. Os corredores do labirinto ficam nas cotas z ±2,7 entre fileiras;
  // spawn de corredor tem que cair nessa faixa, não em cima da fileira.
  spawns: [
    [-21.5, 0, -16.2, 1.7, 'enemy', true],       // cais norte, atrás do guindaste
    [-20.5, 0, 9.0, 1.4, 'enemy', true],         // cais sul
    [-9.0, 0, -4.5, 3.1, 'enemy', true],         // corredor central do labirinto
    [3.0, 0, 9.2, 3.4, 'enemy', true],           // corredor sul do labirinto
    [20.0, 0, -12.0, 4.6, 'enemy', true],        // piso do galpão
    [27.5, NIVEL_ALTO, -10.0, 4.4, 'sniper', false],  // passarela do galpão
    [20.0, GALPAO.TELHADO, -6.6, 3.6, 'sniper', false], // telhado, atrás da sacaria
    [22.5, 3.3, 12.5, 4.2, 'sniper', false],     // primeiro andar da administração
    [-1.5, 0, -26.0, 0.2, 'enemy', true],        // pátio ferroviário
    [29.0, 0, 3.0, 4.7, 'enemy', true],          // corredor leste, junto ao muro
    [-3.0, 5.2, -7.2, 3.2, 'sniper', false],     // topo da pilha central (2 níveis = 5,2)
    [-14.0, 0, 20.0, 0.6, 'patrol', true],
    [8.0, 0, 22.0, 3.0, 'patrol', true],
    [16.0, 0, -19.0, 2.4, 'patrol', true],
    [-22.0, 0, 3.0, 1.5, 'patrol', true],
    [6.0, 0, -15.0, 3.3, 'patrol', true],
    [26.0, 0, 22.0, 3.8, 'patrol', true],
    [-8.0, 0, 26.0, 0.4, 'patrol', true],
  ],

  // [x, y, z, cor, intensidade, alcance, sombra]
  // Sódio alaranjado é a única cor quente do mapa. É de propósito: num pátio
  // inteiro em azul acinzentado, cada lâmpada vira ponto de referência.
  luzes: [
    [20.0, 5.6, -13.0, 0xffd0a0, 44, 24, true],    // vão principal do galpão
    [16.5, 3.0, -6.5, 0xffc890, 16, 12, false],    // canto sul do galpão
    [22.5, 2.9, 12.0, 0xffbe86, 20, 14, false],    // térreo da administração
    [-19.5, 4.2, -6.0, 0xffd8b0, 26, 18, false],   // casa de máquinas do guindaste norte
    [-2.0, 2.7, 28.5, 0xfff0c8, 14, 11, false],    // guarita do portão sul
    [1.0, 2.6, -1.0, 0xffcf9a, 18, 13, true],      // poste no cruzamento do labirinto
  ],
  // Só a do galpão e a do cruzamento piscam: reator velho de vapor de sódio
  // pisca, lâmpada de escritório não.
  piscam: [0, 5],

  ambiente: {
    // 12 graus é sol de meio da tarde no Báltico em janeiro: sombra longuíssima
    // e luz raspando. Só que com o sol tão baixo o cosseno na horizontal vale
    // 0,2 — o pátio inteiro depende do céu, não do disco. Os primeiros valores
    // (4,6) davam 0,185 de média medida no chão: um crepúsculo subexposto, não
    // uma tarde. 7,0 devolve modelado às paredes sem clarear o piso.
    sol: { azimute: 208, elevacao: 12, intensidade: 7.0, cor: 0xffe8d8 },
    // Teto encoberto: o zênite tem que ficar QUASE tão claro quanto o horizonte
    // — é essa diferença pequena que lê como nuvem, e a diferença grande que lê
    // como céu limpo de fim de tarde. Turbidez baixa e cor de sol quase neutra
    // pelo mesmo motivo: com o disco a 12 graus, um halo quente forte no
    // horizonte transforma nuvem em pôr do sol na hora (foi o que aconteceu com
    // turbidez 0.55 e zênite 0x6e7f90 — a captura saía roxa e alaranjada).
    ceu: { zenite: 0xa2b0ba, horizonte: 0xd2d8da, chao: 0xa0a5a8, corSol: 0xf4efe6, turbidez: 0.22, neblina: 0.55 },
    // Sob nuvem, quase toda a luz vem de cima e é difusa e fria. Hemi forte é o
    // que faz a cena ler como dia encoberto em vez de noite mal exposta — e é o
    // único termo que chega ao pátio, já que o asfalto quase não vê o sol.
    hemi: { ceu: 0xc6d2da, chao: 0x8d9196, intensidade: 8.5 },
    // Névoa de porto: densa o bastante para o muro do fundo sumir, e da MESMA
    // cor do horizonte do céu — quando ela é mais escura, tudo que está longe
    // fica mais escuro que o céu atrás e a silhueta recorta ao contrário.
    fog: { cor: 0xc3ccd2, densidade: 0.0125 },
    paleta: {
      terreno: 'asphalt', piso: 'concrete', parede: 'concrete', estrutura: 'concrete',
      metal: 'metalPainted', ferro: 'gunMetal', madeira: 'woodPlank',
      ferrugem: 'rust', pano: 'fabric', gelo: 'tile', agua: 'gunMetal',
    },
  },

  build(kit, ctx) {
    const rng = ctx.rng;
    // --- FASE 1: geometria ---------------------------------------------------
    chao(kit, rng);
    bacia(kit, rng);
    perimetro(kit, rng);
    cais(kit);
    galpao(kit);
    administracao(kit);
    labirinto(kit, rng);
    ferrovia(kit);
    passarelas(kit);
    // --- FASE 2: espalhamento (precisa dos colisores já fechados) -------------
    espalhamento(kit, rng);
  },
};

// ---------------------------------------------------------------------------
// chão do pátio: asfalto sujo, neve encardida nas bordas, poças de gelo
// ---------------------------------------------------------------------------

function chao(kit, rng) {
  kit.groundPlane('asphalt', { size: 180, repeat: 60, name: 'ground' });
  // Laje de concreto do cais, mais clara que o asfalto do pátio: é o que separa
  // as duas zonas a olho, sem precisar de linha pintada.
  kit.deco('concrete', -20.6, 0.012, 0, 9.0, 0.02, 62, { density: 0.5, tint: [0.86, 0.9, 0.95] });

  // Neve suja: nunca no meio do pátio (por onde passa caminhão), sempre
  // encostada em alguma coisa. Espalhar uniformemente mataria essa leitura.
  const faixas = [
    [-25.0, 0, 1.4, 60], [33.0, 0, 1.6, 62], [0, -33.0, 62, 1.8], [0, 33.0, 60, 1.6],
    [12.0, -23.6, 20, 1.2], [31.6, -13, 1.2, 20], [-16.5, 20.5, 26, 1.2],
  ];
  for (const [x, z, sx, sz] of faixas) {
    kit.deco('concrete', x, 0.05, z, sx, 0.09, sz, { density: 0.4, tint: [1.55, 1.62, 1.72] });
  }
  for (let i = 0; i < 34; i++) {
    const x = kit.rand(-24, 32), z = kit.rand(-32, 32);
    kit.deco('concrete', x, 0.04, z, 1.6 + rng() * 4.5, 0.06, 1.4 + rng() * 3.5,
      { rotY: rng() * 3.14, density: 0.35, tint: [1.4 + rng() * 0.25, 1.48 + rng() * 0.25, 1.6 + rng() * 0.25] });
  }
  // Poças congeladas: 'tile' é a superfície mais lisa do conjunto, então é a
  // única que devolve reflexo especular de céu — é o reflexo que lê como gelo.
  for (let i = 0; i < 16; i++) {
    const x = kit.rand(-22, 31), z = kit.rand(-30, 31);
    kit.deco('tile', x, 0.032, z, 1.2 + rng() * 3.0, 0.02, 1.0 + rng() * 2.4,
      { rotY: rng() * 3.14, density: 0.6, tint: [1.15, 1.28, 1.45] });
  }
}

// ---------------------------------------------------------------------------
// bacia: água escura, chapas de gelo, o navio atracado no cais oposto
// ---------------------------------------------------------------------------

function bacia(kit, rng) {
  // Lâmina d'água. 'gunMetal' é metálico e liso: reflete o domo de céu, que é
  // o que dá a aparência de água parada e escura sem shader nenhum. Em metal o
  // albedo É a cor do reflexo, e o albedo base do gunMetal é 0,17: com o tint
  // antigo a bacia refletia 9% do céu e media 37% de pixels no preto. Sob nuvem
  // a água fica cinza-azulada, não preta — daí o tint acima de 1.
  kit.deco('gunMetal', -46, AGUA, 0, 44, 0.02, 96, { density: 0.25, tint: [1.5, 1.9, 2.3] });

  // Placas de gelo à deriva, mais densas junto ao cais (onde a água está parada).
  for (let i = 0; i < 30; i++) {
    const x = kit.rand(-40, CAIS_X - 0.6);
    const z = kit.rand(-32, 32);
    const w = 2.0 + rng() * 6.0, d = 1.8 + rng() * 5.0;
    kit.deco('tile', x, AGUA + 0.03, z, w, 0.05, d,
      { rotY: rng() * 3.14, density: 0.45, tint: [1.35, 1.45, 1.6] });
    // borda quebrada, um tom acima: sem ela a placa lê como retângulo pintado
    kit.deco('tile', x + (rng() - 0.5) * w * 0.5, AGUA + 0.055, z + (rng() - 0.5) * d * 0.5,
      w * 0.5, 0.04, d * 0.5, { rotY: rng() * 3.14, density: 0.5, tint: [1.6, 1.68, 1.78] });
  }

  // --- cais oposto: fecha o oeste com ficção, não com parede invisível -------
  kit.wall('concrete', -34.6, -34, -34.6, 34, 0, 2.6, 1.2,
    { tint: [0.8, 0.84, 0.9], plinth: false, trim: 0.16 });
  // silhuetas do outro lado da bacia, atrás do limite jogável: dão profundidade
  // e explicam por que o mapa acaba ali
  // Tint acima de 1: são metais (o albedo é a cor do reflexo) a 20 m de
  // distância e quase sem névoa nessa faixa. Escurecidos, viravam recortes
  // pretos contra o céu claro em vez de estrutura distante — o oposto do que
  // profundidade aérea faz.
  for (const [x, z, w, h, d] of [[-40, -22, 9, 9.5, 14], [-42, 4, 12, 7.0, 18], [-38, 22, 7, 5.5, 9]]) {
    kit.deco('metalPainted', x, h * 0.5, z, w, h, d, { density: 0.5, tint: [1.15, 1.25, 1.35] });
  }
  for (const z of [-14, 12]) portico(kit, -40, z, 9.0, 15.0, [1.1, 1.2, 1.32]);

  // --- navio de carga atracado no cais oposto --------------------------------
  const casco = { x: -31.6, z: -3, L: 40, W: 7.2, H: 4.6 };
  kit.solid('rust', casco.x, casco.H * 0.5, casco.z, casco.W, casco.H, casco.L,
    { density: 0.55, tint: [1.05, 0.78, 0.62] });
  // faixa de linha d'água e costados: um casco liso lê como caixa
  kit.deco('rust', casco.x, 0.8, casco.z, casco.W + 0.16, 1.0, casco.L,
    { density: 0.7, tint: [0.5, 0.42, 0.4] });
  for (let z = casco.z - casco.L / 2 + 2; z < casco.z + casco.L / 2; z += 4) {
    kit.deco('rust', casco.x + casco.W * 0.5 + 0.05, 3.0, z, 0.12, 2.6, 0.5,
      { density: 1.4, tint: [0.9, 0.72, 0.6] });
  }
  // superestrutura à ré, chaminé e guindaste de bordo
  kit.deco('metalPainted', casco.x, 6.6, casco.z + 14.0, casco.W - 0.8, 4.0, 7.0,
    { density: 0.7, tint: [1.5, 1.55, 1.6] });
  kit.deco('metalPainted', casco.x, 9.6, casco.z + 15.5, 3.0, 2.2, 3.0,
    { density: 0.8, tint: [1.4, 1.45, 1.5] });
  kit.cylinder('rust', casco.x, 11.6, casco.z + 12.6, 0.9, 3.4, { tint: [1.0, 0.72, 0.6], seg: 12 });
  kit.mast(casco.x, 10.6, casco.z + 17.5, 5.0);
  for (const z of [casco.z - 12, casco.z - 1, casco.z + 6]) {
    kit.deco('metalPainted', casco.x, 5.4, z, casco.W - 1.4, 1.6, 5.0, { density: 0.8, tint: [1.2, 1.24, 1.3] });
  }
  // amarras do navio até os cabeços do nosso cais
  for (const [zn, zc] of [[casco.z - 14, -16], [casco.z + 2, 2], [casco.z + 16, 18]]) {
    kit.cable([casco.x + 3.4, 4.4, zn], [CAIS_X + 0.7, 0.4, zc], 1.1, 0.06);
  }
}

/** Pórtico de guindaste visto de longe: só silhueta, sem colisor. */
function portico(kit, x, z, vao, altura, tint) {
  for (const s of [-1, 1]) {
    kit.deco('gunMetal', x + s * vao * 0.5, altura * 0.5, z, 0.7, altura, 0.7, { density: 1.2, tint });
    kit.deco('gunMetal', x + s * vao * 0.5, altura * 0.5, z + 6, 0.7, altura, 0.7, { density: 1.2, tint });
  }
  kit.deco('gunMetal', x, altura + 0.6, z + 3, vao + 2, 1.2, 7.0, { density: 1.0, tint });
  kit.deco('gunMetal', x + vao * 0.9, altura + 0.2, z + 3, vao * 1.4, 0.9, 2.4, { density: 1.0, tint });
}

// ---------------------------------------------------------------------------
// perímetro: muro de contenção ao norte e leste, portão ao sul
// ---------------------------------------------------------------------------

function perimetro(kit, rng) {
  const T = 0.65;
  // Muro pré-moldado: painéis de 4 m entre pilastras. A crista sorteada por
  // painel evita a leitura de "uma caixa comprida".
  const lados = [
    // [ax, az, bx, bz, vaos[]]   vão = [u0, u1] deixado sem painel
    [-25.0, -33.8, 34.0, -33.8, [[-9.5, -1.0]]],   // norte, aberto onde entram os trilhos
    [34.0, -33.8, 34.0, 34.0, []],                 // leste
    [-25.0, 33.8, 34.0, 33.8, [[-4.5, 4.5]]],      // sul, portão principal
  ];
  for (const [ax, az, bx, bz, vaos] of lados) {
    const horiz = Math.abs(bx - ax) >= Math.abs(bz - az);
    const s0 = horiz ? ax : az, s1 = horiz ? bx : bz;
    const fixo = horiz ? az : ax;
    const n = Math.max(1, Math.round((s1 - s0) / 4));
    for (let i = 0; i < n; i++) {
      const a = kit.lerp(s0, s1, i / n), b = kit.lerp(s0, s1, (i + 1) / n);
      if (vaos.some((v) => b > v[0] && a < v[1])) continue;
      const h = 3.5 + rng() * 0.3;
      const tint = [0.86 + rng() * 0.1, 0.9 + rng() * 0.1, 0.96 + rng() * 0.08];
      kit.solid('concrete', horiz ? (a + b) * 0.5 : fixo, h * 0.5, horiz ? fixo : (a + b) * 0.5,
        horiz ? b - a : T, h, horiz ? T : b - a, { tint, uoff: a * 0.5, voff: a * 0.5 });
      // capa de neve na crista do painel — o detalhe que datou o mapa no inverno
      kit.deco('concrete', horiz ? (a + b) * 0.5 : fixo, h + 0.06, horiz ? fixo : (a + b) * 0.5,
        horiz ? b - a : T + 0.14, 0.12, horiz ? T + 0.14 : b - a, { tint: [1.6, 1.66, 1.74], density: 0.8 });
    }
    // pilastras a cada 8 m
    for (let u = s0 + 4; u < s1; u += 8) {
      kit.deco('concrete', horiz ? u : fixo, 1.9, horiz ? fixo : u,
        horiz ? 0.55 : T + 0.2, 3.8, horiz ? T + 0.2 : 0.55, { tint: [0.82, 0.86, 0.92], density: 0.7 });
    }
  }

  // Cerca de tela fechando o vão dos trilhos ao norte: passagem estreita e
  // óbvia, com portão. O muro não podia simplesmente parar ali.
  kit.fence(-9.5, -33.4, -1.0, -33.4, 2.4, [[-6.5, -3.5]], { gate: true });

  // --- portão sul ------------------------------------------------------------
  for (const s of [-1, 1]) {
    kit.solid('concrete', s * 5.0, 2.4, 33.8, 1.0, 4.8, 1.0, { tint: [0.86, 0.9, 0.95], density: 0.7 });
    kit.deco('concrete', s * 5.0, 4.95, 33.8, 1.24, 0.3, 1.24, { tint: [1.5, 1.56, 1.64] });
  }
  kit.solid('gunMetal', 0, 4.5, 33.8, 9.2, 0.5, 0.5, { density: 1.6, tint: [0.9, 0.95, 1.0] });
  kit.deco('metalPainted', 0, 3.9, 33.8, 8.0, 0.6, 0.12, { tint: [1.1, 1.15, 1.2], density: 1.2 });
  // folha do portão corrediça, recolhida
  kit.deco('metalPainted', -7.6, 1.4, 33.4, 5.0, 2.8, 0.12, { density: 1.0, tint: [0.9, 0.94, 0.98] });

  // guarita do portão
  kit.wall('concrete', -4.6, 26.8, -4.6, 30.2, 0, 2.8, 0.25,
    { openings: [{ u0: 27.6, u1: 29.4, y0: 1.0, y1: 2.2 }], tint: [1.0, 1.02, 1.05], trim: 0.14 });
  kit.wall('concrete', 0.4, 26.8, 0.4, 30.2, 0, 2.8, 0.25,
    { openings: [{ u0: 27.6, u1: 29.4, y0: 1.0, y1: 2.2 }], tint: [1.0, 1.02, 1.05], trim: 0.14 });
  kit.wall('concrete', -4.6, 26.8, 0.4, 26.8, 0, 2.8, 0.25,
    { openings: [{ u0: -3.6, u1: -2.0, y0: 0, y1: 2.2 }], tint: [1.0, 1.02, 1.05], trim: 0.14 });
  kit.wall('concrete', -4.6, 30.2, 0.4, 30.2, 0, 2.8, 0.25,
    { openings: [{ u0: -3.2, u1: -1.0, y0: 1.0, y1: 2.2 }], tint: [1.0, 1.02, 1.05], trim: 0.14 });
  kit.slab('concrete', -4.9, 0.7, 26.5, 30.5, 3.1, 0.28, [], { tint: [0.88, 0.92, 0.96] });
  kit.deco('concrete', -2.1, 3.24, 28.5, 6.0, 0.16, 4.6, { tint: [1.55, 1.6, 1.68], density: 0.8 });
}

// ---------------------------------------------------------------------------
// cais: muro de borda, cabeços, dois guindastes de pórtico com acesso
// ---------------------------------------------------------------------------

function cais(kit) {
  // Muro de borda de 0,95 m: alto demais para atravessar andando, baixo o
  // bastante para o mantle vencer. Passar para o gelo é decisão do jogador, não
  // acidente — e do outro lado ele fica visível de meio mapa.
  kit.solid('concrete', CAIS_X - 0.25, 0.48, 0, 0.5, 0.96, 66, { density: 0.7, tint: [0.84, 0.88, 0.93] });
  kit.deco('concrete', CAIS_X - 0.25, 1.02, 0, 0.7, 0.12, 66, { tint: [1.5, 1.56, 1.64], density: 0.9 });
  // faixa de advertência amarela recuada da borda
  kit.deco('concrete', CAIS_X + 0.9, 0.028, 0, 0.35, 0.02, 64, { density: 1.2, tint: [1.9, 1.55, 0.7] });

  // cabeços de amarração
  for (let z = -24; z <= 24; z += 8) {
    kit.cylinder('gunMetal', CAIS_X + 0.75, 0.3, z, 0.34, 0.6, { seg: 10, solid: true, tint: [0.95, 0.98, 1.0] });
    kit.deco('gunMetal', CAIS_X + 0.75, 0.66, z, 0.86, 0.16, 0.86, { density: 1.6, tint: [0.9, 0.93, 0.96] });
  }
  // escadas de mão embutidas na borda (só enfeite, mas explicam a borda)
  for (const z of [-18, 6]) {
    for (let i = 0; i < 3; i++) {
      kit.deco('gunMetal', CAIS_X - 0.55, 0.15 + i * 0.3, z, 0.1, 0.06, 0.6, { density: 2 });
    }
  }

  // Trilho dos guindastes: duas linhas correndo o cais inteiro.
  for (const x of [-23.4, -17.6]) {
    kit.deco('gunMetal', x, 0.06, 0, 0.16, 0.12, 62, { density: 2.5, tint: [1.3, 1.35, 1.4] });
  }

  guindaste(kit, -12.0, -9.8);    // guindaste norte — escada pelo corredor de z=-9,8
  guindaste(kit, 10.0, 11.7);     // guindaste sul  — escada pelo corredor de z=11,7
}

/**
 * Guindaste de pórtico sobre o cais. As pernas são sólidas (cobertura dura no
 * meio do flanco aberto) e OS DOIS carregam escada: são as duas entradas da
 * rota vertical cais -> plataforma -> passarela -> topo das pilhas.
 *
 * `zEscada` é o corredor por onde o lance sobe, e não é gosto:
 *
 * - o do NORTE subia rente à perna leste, em x=-16,4, de z=-15,4 para z=-10,0.
 *   Só que a pilha de x=-18,0 da fileira z=-12,4 ocupa x -21..-15: o lance
 *   entrava DENTRO do contêiner a partir de z=-13,6. `tools/test-stairs.mjs`
 *   mediu: o jogador andando para frente parava em y=1,77 m dos 5,31, com a
 *   cintura na chapa da pilha. Nenhuma medida de ALCANCE reclamava, porque
 *   escalando a lateral do lance dava para chegar em cima — é a armadilha 2 do
 *   NOTES-mapas em estado puro, e por isso a escada mais antiga do mapa passou
 *   meses quebrada;
 * - o do SUL nunca teve escada; o lugar equivalente ao lance antigo do norte é
 *   ocupado pela pilha de x=-17,2 da fileira z=9,0.
 *
 * Os dois agora sobem deitados no corredor entre duas fileiras (2,76 m e
 * 2,96 m de vão livre, contra 1,2 m de lance) e chegam à borda LESTE do deck,
 * que é o único lado sem guarda-corpo. Nada por cima em todo o lance.
 */
function guindaste(kit, z, zEscada) {
  const XA = -23.4, XB = -17.6;           // pernas sobre os dois trilhos
  const H = NIVEL_ALTO;                    // altura da plataforma de serviço
  const tint = [1.25, 1.3, 1.36];

  for (const x of [XA, XB]) {
    for (const dz of [-3.2, 3.2]) {
      kit.solid('metalPainted', x, H * 0.5, z + dz, 0.75, H, 0.75, { density: 1.0, tint });
      // contraventamento diagonal, só enfeite
      kit.deco('gunMetal', x, H * 0.55, z + dz * 0.5, 0.16, 0.16, 6.6, { density: 2, tint: [1.0, 1.05, 1.1] });
    }
    // travessa que fecha o pórtico no sentido do cais
    kit.deco('metalPainted', x, H - 0.5, z, 0.8, 0.7, 7.2, { density: 1.0, tint });
  }
  // Plataforma de serviço entre as pernas: piso de verdade, com guarda-corpo.
  kit.catwalk('metalPainted', XA - 0.6, XB + 0.6, z - 3.6, z + 3.6, H, { rails: ['w', 'n', 's'], thick: 0.26 });

  // torre acima da plataforma + lança avançando sobre a água
  kit.deco('metalPainted', (XA + XB) * 0.5, H + 5.5, z, 2.4, 11.0, 2.4, { density: 0.8, tint });
  kit.deco('gunMetal', (XA + XB) * 0.5 - 9.5, H + 10.4, z, 20.0, 0.9, 1.6, { density: 1.2, tint: [1.0, 1.05, 1.1] });
  kit.deco('gunMetal', (XA + XB) * 0.5 + 5.0, H + 10.0, z, 8.0, 0.7, 1.3, { density: 1.2, tint: [1.0, 1.05, 1.1] });
  kit.cable([(XA + XB) * 0.5, H + 11.2, z], [(XA + XB) * 0.5 - 18.0, H + 10.9, z], 0.9, 0.06);
  // cabo de içamento com o gancho parado no ar
  kit.cable([(XA + XB) * 0.5 - 13.0, H + 10.0, z], [(XA + XB) * 0.5 - 13.0, 2.6, z], 0.0, 0.05);
  kit.deco('gunMetal', (XA + XB) * 0.5 - 13.0, 2.1, z, 0.5, 1.0, 0.5, { density: 1.6, tint: [0.8, 0.84, 0.88] });

  // casa de máquinas encostada na perna leste — interior mínimo, mas é abrigo
  kit.deco('metalPainted', XB + 1.9, H + 1.4, z, 2.6, 2.6, 3.4, { density: 0.9, tint: [1.15, 1.2, 1.26] });

  // Escada de aço até a plataforma. rise 0.295 < 0.42 (STEP_HEIGHT): sobe
  // andando, sem pular — é o que `tools/test-stairs.mjs` cobra nos dois casos.
  // 18 x 0,295 = 5,31 m, começando no corredor em x=-11,6 e terminando em
  // x=-17,0, que é exatamente a borda leste do deck.
  kit.stairs('gunMetal', -11.6, zEscada, -1, 0, 18, 0.295, 0.30, 1.2, 0,
    { rail: 'both', tint: [1.1, 1.15, 1.2] });
}

// ---------------------------------------------------------------------------
// galpão de carga a nordeste — o ponto alto disputado
// ---------------------------------------------------------------------------

function galpao(kit) {
  const M = 'metalPainted';
  const { W, E, N, S, H, TELHADO } = GALPAO;
  const t = 0.42;
  const tint = [1.12, 1.18, 1.24];
  const parede = { tint, ribs: 1.2, plinth: true, trim: 0 };

  // fachada oeste: portão de carga, porta de gente e o vão alto da passarela
  kit.wall(M, W, N, W, S, 0, H, t, {
    ...parede,
    openings: [
      { u0: -16.0, u1: -10.6, y0: 0, y1: 5.0, frame: false },        // portão de carga
      { u0: -8.0, u1: -6.6, y0: 0, y1: 2.3 },                        // porta de serviço
      { u0: -13.6, u1: -11.0, y0: NIVEL_ALTO - 0.3, y1: NIVEL_ALTO + 1.9, frame: false }, // chegada da passarela
      { u0: -20.4, u1: -18.4, y0: 5.8, y1: 6.9, frame: false },
    ],
  });
  kit.wall(M, E, N, E, S, 0, H, t, {
    ...parede,
    openings: [
      { u0: -19.0, u1: -17.0, y0: 5.8, y1: 6.9, frame: false },
      { u0: -12.0, u1: -10.0, y0: 5.8, y1: 6.9, frame: false },
      { u0: -7.0, u1: -5.6, y0: 0, y1: 2.3 },
    ],
  });
  kit.wall(M, W, N, E, N, 0, H, t, {
    ...parede,
    openings: [
      { u0: 18.0, u1: 22.5, y0: 0, y1: 4.2, frame: false },          // portão norte, rasgado
      { u0: 25.0, u1: 27.0, y0: 5.8, y1: 6.9, frame: false },
    ],
  });
  kit.wall(M, W, S, E, S, 0, H, t, {
    ...parede,
    openings: [
      { u0: 14.6, u1: 18.4, y0: 0, y1: 4.0, frame: false },          // portão sul
      { u0: 22.0, u1: 24.0, y0: 5.8, y1: 6.9, frame: false },
      { u0: 27.0, u1: 29.0, y0: 5.8, y1: 6.9, frame: false },
    ],
  });

  // telhado de chapa, com alçapão sobre a escada íngreme
  kit.slab(M, W - 0.3, E + 0.3, N - 0.3, S + 0.3, TELHADO, 0.4, [[27.4, 29.4, -9.0, -6.2]],
    { tint: [1.05, 1.12, 1.18] });
  kit.parapet(M, W - 0.1, E + 0.1, N - 0.1, S + 0.1, TELHADO, 0.75, 0.26,
    [['w', -14.0, -11.4], ['s', 20.0, 22.5], ['n', 16.0, 18.0], ['e', -16.0, -13.5]]);
  // nervuras da chapa: é o que lê como telhado metálico a 40 m
  for (let x = W + 0.6; x < E; x += 1.5) {
    kit.deco(M, x, TELHADO + 0.07, (N + S) * 0.5, 0.45, 0.14, S - N - 0.6,
      { tint: [1.0, 1.06, 1.12], density: 1.1 });
  }
  // neve acumulada nas águas do telhado
  for (const z of [N + 2.4, (N + S) * 0.5, S - 2.4]) {
    kit.deco('concrete', (W + E) * 0.5, TELHADO + 0.16, z, E - W - 3.0, 0.1, 3.0,
      { tint: [1.55, 1.62, 1.72], density: 0.5 });
  }
  // telhas translúcidas: dizem "há luz lá dentro" antes de o jogador entrar
  for (const z of [-18, -12, -6]) {
    kit.deco(M, 21.5, H - 0.02, z, 7.0, 0.06, 1.3, { tint: [1.6, 1.65, 1.7], density: 0.6 });
  }

  // --- interior --------------------------------------------------------------
  kit.deco('concrete', (W + E) * 0.5, 0.03, (N + S) * 0.5, E - W - 0.6, 0.02, S - N - 0.6,
    { density: 0.7, tint: [0.9, 0.93, 0.96] });
  // passarela em L nas paredes leste e norte, na mesma cota da rede externa
  kit.catwalk(M, 26.8, 30.4, -21.6, -5.2, NIVEL_ALTO, { rails: [] });
  kit.catwalk(M, 12.8, 26.8, -21.6, -19.0, NIVEL_ALTO, { rails: [] });
  // Guarda-corpo oeste em DOIS lances: o vão entre eles é por onde se chega da
  // escada. Corrimão é colisor (ver `rail` no MapKit), então um lance corrido
  // aqui seria uma parede de 1 m fechando a única entrada da passarela.
  kit.rail(26.8, -21.6, 26.8, -14.2, NIVEL_ALTO);
  kit.rail(26.8, -12.8, 26.8, -5.2, NIVEL_ALTO);
  kit.rail(12.8, -19.0, 26.8, -19.0, NIVEL_ALTO);
  kit.rail(26.8, -5.2, 30.4, -5.2, NIVEL_ALTO);
  // escada piso -> passarela e passarela -> alçapão do telhado
  //
  // A escada sobe RENTE à borda oeste do deck (x 26,8), não debaixo dele. Ela
  // estava em x=28,4, no meio da passarela: subia por baixo da própria laje que
  // era o seu destino e a cabeça batia no fundo do deck a 3,2 m — o jogador
  // travava no meio do lance e a rede alta inteira, 635 m² de galpão e
  // passarelas, ficava inalcançável a pé. Era isto que fazia a passarela
  // "terminar no ar" vista de cima. Os 1,7 m de largura em x=26,0 encostam no
  // deck (25,15..26,85 contra 26,8), de propósito: sem essa sobreposição
  // sobraria um vão de alguns centímetros no ponto exato da chegada.
  //
  // O pé em z=-6,0 (e não -4,4, como estava) é a segunda metade do conserto: a
  // empena sul fica em z≈-4,2 e o primeiro degrau encostava nela, sem piso na
  // frente para entrar. Dava para chegar lá em cima escalando a LATERAL da
  // escada, então nenhuma medida de alcance reclamava — mas ninguém sobe de
  // frente uma escada que começa dentro da parede. Agora sobram 1,8 m de piso
  // de aproximação, e o topo cai em z≈-13,6, onde o guarda-corpo abre.
  kit.stairs(M, 26.0, -6.0, 0, -1, 18, 0.295, 0.42, 1.7, 0, { rail: 'left', tint: [1.1, 1.16, 1.22] });
  kit.stairs(M, 28.4, -6.4, 0, -1, 9, 0.256, 0.3, 1.5, NIVEL_ALTO, { rail: 'both', tint: [1.1, 1.16, 1.22] });

  // estantes: é o que faz o galpão jogar diferente de um quadrado vazio
  for (const z of [-17.0, -13.4, -8.6]) kit.rack({ x0: 14.0, x1: 18.0, z });
  kit.rack({ x0: 22.0, x1: 26.0, z: -17.0 });

  // escritório de expedição no canto sudoeste, com laje pisável em cima
  kit.wall(M, 13.0, -8.6, 18.2, -8.6, 0, 2.9, 0.2,
    { openings: [{ u0: 16.4, u1: 17.8, y0: 0, y1: 2.2 }], tint, plinth: false });
  kit.wall(M, 18.2, -8.6, 18.2, -4.4, 0, 2.9, 0.2,
    { openings: [{ u0: -7.6, u1: -5.8, y0: 1.0, y1: 2.3 }], tint, plinth: false });
  kit.slab(M, 12.8, 18.4, -8.8, -4.2, 3.1, 0.2, [], { tint: [1.05, 1.1, 1.16] });
  kit.deco(M, 15.6, 3.3, -6.5, 5.4, 0.2, 4.4, { tint: [1.5, 1.55, 1.62], density: 0.8 });
}

// ---------------------------------------------------------------------------
// prédio da administração a sudeste — dois pavimentos, janela para o pátio
// ---------------------------------------------------------------------------

function administracao(kit) {
  const b = kit.building({
    surface: 'concrete',
    x0: 17.4, x1: 27.6, z0: 6.4, z1: 17.6,
    t: 0.42, floors: 2, floorHeight: 3.3, base: 0,
    tint: [0.94, 0.97, 1.02], trim: 0.2,
    slabTint: [0.9, 0.93, 0.97],
    roofTint: [0.88, 0.92, 0.96],
    overhang: 0.35,
    // O vão sul é a chegada da escada externa: sem ele o parapeito de 85 cm
    // fecharia o último passo, e a escada acabaria encostada num muro.
    parapeito: { h: 0.85, t: 0.28, gaps: [['w', 9.0, 11.0], ['n', 21.0, 23.0], ['s', 25.6, 27.4]] },
    holes: [{ andar: 1, rect: [24.6, 27.2, 7.0, 10.0] }],   // vazio da escada
    openings: [
      // térreo
      { lado: 'w', andar: 0, u0: 9.2, u1: 11.4, y0: 0, y1: 2.4 },
      { lado: 'w', andar: 0, u0: 13.6, u1: 15.8, y0: 1.0, y1: 2.4 },
      { lado: 'n', andar: 0, u0: 19.0, u1: 21.4, y0: 1.0, y1: 2.4 },
      { lado: 's', andar: 0, u0: 22.0, u1: 24.4, y0: 0, y1: 2.4 },
      { lado: 'e', andar: 0, u0: 12.0, u1: 14.2, y0: 1.0, y1: 2.4 },
      // primeiro andar: a janela larga a oeste é a linha de tiro sobre o pátio
      { lado: 'w', andar: 1, u0: 8.6, u1: 12.6, y0: 0.9, y1: 2.5 },
      { lado: 'w', andar: 1, u0: 14.0, u1: 16.0, y0: 0.9, y1: 2.5 },
      { lado: 'n', andar: 1, u0: 18.6, u1: 20.6, y0: 0.9, y1: 2.5 },
      { lado: 'n', andar: 1, u0: 23.4, u1: 25.4, y0: 0.9, y1: 2.5 },
      { lado: 's', andar: 1, u0: 19.0, u1: 21.0, y0: 0.9, y1: 2.5 },
      { lado: 'e', andar: 1, u0: 8.6, u1: 10.6, y0: 0.9, y1: 2.5 },
    ],
  });
  // escada interna térreo -> primeiro, dentro do vazio da laje
  kit.stairs('concrete', 25.0, 10.0, 0, -1, 12, 0.275, 0.42, 1.5, 0, { rail: 'left' });
  // Escada externa até o telhado, rente à empena sul.
  //
  // Aqui havia três peças que não se encostavam: um lance do chão que subia
  // para DENTRO da empena, um patamar a 3,4 m e um segundo lance que subia para
  // o SUL, afastando-se do prédio e terminando a 7,6 m no ar, a sete metros da
  // laje. O telhado — 158 m² — não tinha acesso a pé por lugar nenhum. Um lance
  // reto e contínuo rasgando a empena de oeste para leste resolve as três
  // coisas de uma vez, e é o que um prédio de porto teria de verdade: escada de
  // serviço externa, não escadaria de praça.
  //
  // 22 degraus de 30 cm dão exatamente os 6,6 m dos dois pavimentos.
  //
  // O eixo em z=19,3 não é folga estética: beiral e parapeito avançam sobre o
  // pátio até z≈18,2, e a primeira versão desta escada, em z=18,4, punha o
  // corpo do jogador exatamente embaixo dessa aba — ele subia 4,5 m dos 6,6 e
  // travava com a cabeça no beiral. É o MESMO defeito que a escada do galpão
  // tinha, e não se vê em captura nenhuma: só andando.
  kit.stairs('concrete', 17.8, 19.3, 1, 0, 22, 0.3, 0.42, 1.5, 0, { rail: 'both' });
  // Patamar de chegada, encaixado no vão do parapeito: liga o topo da escada à
  // borda da laje. Sem ele sobra um vão de 30 cm de ar entre o último degrau e
  // o beiral — a escada chegaria à altura certa e ainda assim ninguém passaria.
  kit.catwalk('concrete', 26.2, 27.7, 17.9, 19.7, 6.6, { rails: [] });

  // neve no parapeito e caixas d'água no telhado
  kit.deco('concrete', 22.5, b.roofY + 0.1, 12.0, 9.4, 0.1, 10.4, { tint: [1.5, 1.58, 1.68], density: 0.5 });
  kit.tank(19.6, b.roofY, 9.0);
  kit.acUnit(25.0, b.roofY + 0.45, 14.5);
  kit.deco('metalPainted', 22.0, 2.9, 6.2, 5.0, 0.5, 0.9, { tint: [1.2, 1.25, 1.3], density: 1.2 });
}

// ---------------------------------------------------------------------------
// labirinto de contêineres — o miolo do mapa
// ---------------------------------------------------------------------------

/**
 * Pilha de contêineres. O kit desenha um casco corrugado de altura cheia (um
 * colisor só, que é o certo para uma pilha); as emendas e as castanhas de canto
 * intermediárias são acrescentadas aqui para a pilha ler como N contêineres
 * empilhados e não como uma caixa gigante.
 */
function pilha(kit, x, z, yaw, niveis, cor, comprimento = 6.0) {
  const h = niveis * 2.6;
  kit.container(x, z, yaw, h, { tint: CORES_CONTEINER[cor % CORES_CONTEINER.length], length: comprimento });
  const c = Math.cos(yaw), s = Math.sin(yaw);
  const L = comprimento, W = 2.44;
  for (let n = 1; n < niveis; n++) {
    const y = n * 2.6;
    // emenda entre cascos: chapa saliente + as oito castanhas do encaixe
    kit.deco('rust', x, y, z, L + 0.06, 0.22, W + 0.06,
      { rotY: yaw, density: 1.6, tint: [0.72, 0.7, 0.68] });
    for (const su of [-1, 1]) {
      for (const sv of [-1, 1]) {
        const ox = c * su * (L / 2 - 0.14) + s * sv * (W / 2 - 0.14);
        const oz = -s * su * (L / 2 - 0.14) + c * sv * (W / 2 - 0.14);
        kit.deco('gunMetal', x + ox, y, z + oz, 0.32, 0.5, 0.32, { rotY: yaw, density: 2 });
      }
    }
  }
  // capa de neve no topo
  kit.deco('concrete', x, h + 0.14, z, L - 0.3, 0.1, W - 0.25,
    { rotY: yaw, density: 0.7, tint: [1.55, 1.62, 1.72] });
}

function labirinto(kit, rng) {
  // Fileiras correndo leste-oeste. O corredor entre fileiras tem ~2,7 m: linha
  // de tiro curta, sem espaço para recuar atirando — que é o jogo que o
  // labirinto deve produzir.
  const fileiras = [
    // z,     [x, níveis, cor, comprimento]
    [-17.6, [[-15.4, 2, 0], [-9.2, 1, 1], [-3.0, 2, 2], [3.2, 1, 3], [9.4, 2, 4]]],
    [-12.4, [[-18.0, 1, 5], [-11.8, 2, 2], [0.6, 2, 0], [6.8, 1, 1]]],
    // Sem a pilha de 3.6 aqui, esta fileira ganha um vão de 6,4 m — igual ao que
    // as fileiras de -12.4, -1.8 e 9.0 já têm. Com ela, o labirinto inteiro ao
    // norte desta linha era um tubo sem saída para a navegação: os vãos de 0,2 m
    // entre pilhas não existem na grade de 0,75 m da navgrid, e a IA não
    // conseguia entrar em metade do miolo do mapa. A de -2.6 tem que ficar: é
    // nela que a passarela de x[-6,-4] aterrissa. A pilha não sumiu do mapa,
    // mudou para o pátio ferroviário.
    [-7.2, [[-15.0, 2, 3], [-8.8, 2, 4], [-2.6, 2, 5], [9.8, 1, 0]]],
    [-1.8, [[-17.6, 1, 2], [-11.4, 2, 0], [4.4, 1, 4], [10.6, 2, 3]]],
    [3.6, [[-15.0, 2, 1], [-8.8, 1, 5], [-2.6, 2, 3], [3.6, 2, 2]]],
    [9.0, [[-17.2, 2, 4], [-11.0, 2, 1], [-4.8, 1, 0], [7.6, 2, 5]]],
    [14.4, [[-14.4, 1, 3], [-8.2, 2, 2], [-2.0, 2, 4], [4.2, 1, 1], [10.4, 2, 0]]],
  ];
  for (const [z, itens] of fileiras) {
    for (const [x, niveis, cor] of itens) pilha(kit, x, z, 0, niveis, cor);
  }

  // Duas fileiras curtas atravessadas: sem elas os corredores viram sete
  // pistas paralelas e o jogador nunca se perde.
  pilha(kit, -6.0, 6.3, Math.PI / 2, 2, 4);
  pilha(kit, 6.5, -4.5, Math.PI / 2, 1, 3);
  pilha(kit, 13.0, 6.0, Math.PI / 2, 2, 5);
  pilha(kit, -19.4, 1.0, Math.PI / 2, 1, 2);
  // a que saiu da fileira de -7.2: no pátio ferroviário ela dá cobertura a quem
  // desce dos vagões, e ali não fecha corredor nenhum
  pilha(kit, -1.6, -21.5, Math.PI / 2, 2, 5);

  // Contêiner ABERTO no cruzamento central: interior pequeno, escuro e muito
  // disputado, porque cobre o cruzamento inteiro por dentro.
  aberto(kit, -3.0, 0.9, 0);
  aberto(kit, 8.0, 17.2, Math.PI / 2);

  // Caixotes servindo de degrau para o primeiro nível (mantle vence 1,9 m).
  //
  // O de (12,6, -2,0) estava DENTRO da pilha de x=10,6 da fileira -1,8: a caixa
  // 11,94..13,26 cabia inteira no casco 7,6..13,6, então era geometria enterrada
  // — não dava degrau para nada e nem se via. Mudou para (0,8, 1,2), encostado
  // na porta do contêiner aberto do cruzamento: dali o mantle alcança o teto de
  // 2,6 m, que era o último topo do labirinto sem acesso a pé. É a MESMA
  // chamada, com outras coordenadas: o número de sorteios não muda, e por isso
  // o resto do mapa não anda um milímetro (o rng é um fluxo só).
  for (const [x, z] of [[-6.6, -14.6], [0.8, 1.2], [-13.0, 6.4], [1.0, 12.0]]) {
    kit.crateStack(x, z, 1.15, 1);
  }

  // poste de iluminação do cruzamento
  kit.pole(1.0, -1.0, 6.4, { surface: 'gunMetal', lamp: true, tint: [1.1, 1.15, 1.2], wires: [[-11.4, 5.2, -1.0]] });
  // sujeira: derrames escuros no asfalto sob os contêineres
  for (let i = 0; i < 12; i++) {
    kit.deco('asphalt', kit.rand(-18, 12), 0.026, kit.rand(-18, 15), 1.4 + rng() * 2.6, 0.02, 1.2 + rng() * 2.0,
      { rotY: rng() * 3.14, density: 0.8, tint: [0.55, 0.56, 0.58] });
  }
}

/** Contêiner de porta aberta: quatro chapas e um teto, com interior de verdade. */
function aberto(kit, x, z, yaw) {
  const L = 6.0, W = 2.44, H = 2.6, t = 0.12;
  const c = Math.cos(yaw), s = Math.sin(yaw);
  const at = (u, v) => [x + c * u + s * v, z - s * u + c * v];
  const tint = [1.0, 0.85, 0.72];
  // laterais e fundo
  for (const sv of [-1, 1]) {
    const p = at(0, sv * (W / 2 - t / 2));
    kit.solid('rust', p[0], H * 0.5, p[1], L, H, t, { rotY: yaw, density: 1.2, tint });
  }
  let p = at(-(L / 2 - t / 2), 0);
  kit.solid('rust', p[0], H * 0.5, p[1], t, H, W, { rotY: yaw, density: 1.2, tint });
  // teto
  p = at(0, 0);
  kit.solid('rust', p[0], H - t * 0.5, p[1], L, t, W, { rotY: yaw, density: 1.0, tint });
  kit.deco('concrete', p[0], H + 0.1, p[1], L - 0.3, 0.1, W - 0.25, { rotY: yaw, density: 0.7, tint: [1.55, 1.62, 1.72] });
  // corrugação externa
  for (let u = -L / 2 + 0.4; u < L / 2; u += 0.45) {
    const q = at(u, 0);
    kit.deco('rust', q[0], H * 0.5, q[1], 0.1, H - 0.3, W + 0.08, { rotY: yaw, density: 1.6, tint });
  }
  // folha de porta aberta, girada para fora
  const d = at(L / 2 - 0.1, W / 2 + 1.0);
  kit.deco('rust', d[0], H * 0.5, d[1], 0.1, H - 0.14, 2.2, { rotY: yaw + 0.9, density: 1.3, tint: [0.85, 0.74, 0.66] });
}

// ---------------------------------------------------------------------------
// ferrovia: entra pelo muro norte, atravessa o pátio, morre num para-choque
// ---------------------------------------------------------------------------

function ferrovia(kit) {
  const eixo = -5.4;             // centro da via
  const bitola = 1.435;
  // lastro
  kit.deco('sand', eixo, 0.02, -12.0, 4.2, 0.03, 44, { density: 0.5, tint: [0.62, 0.64, 0.68] });
  // dormentes: um só lote, instância seria exagero para 40 caixas
  for (let z = -33; z < 10; z += 0.75) {
    kit.deco('woodPlank', eixo, 0.07, z, 2.6, 0.1, 0.26, { density: 1.4, tint: [0.55, 0.54, 0.56] });
  }
  for (const dx of [-bitola / 2, bitola / 2]) {
    kit.deco('gunMetal', eixo + dx, 0.16, -12.0, 0.14, 0.14, 44, { density: 3, tint: [1.35, 1.4, 1.45] });
  }
  // para-choque no fim da linha
  kit.solid('gunMetal', eixo, 0.55, 10.4, 2.6, 1.1, 0.7, { density: 1.4, tint: [1.2, 0.9, 0.7] });
  kit.deco('gunMetal', eixo, 1.2, 10.4, 2.2, 0.35, 0.3, { density: 1.6, tint: [1.8, 1.3, 0.6] });

  // dois vagões parados: cobertura alta no pátio ferroviário e degrau para cima
  vagao(kit, eixo, -28.0, true);
  vagao(kit, eixo, -19.0, false);
}

/** Vagão sobre a via: plataforma pisável em cima, truques e para-choque. */
function vagao(kit, x, z, fechado) {
  const L = 8.0, W = 3.0;
  const piso = 1.15;
  kit.solid('rust', x, piso * 0.5 + 0.25, z, W, piso - 0.5, L, { density: 0.9, tint: [0.95, 0.72, 0.6] });
  kit.solid('rust', x, piso + 0.06, z, W + 0.3, 0.12, L + 0.4, { density: 0.8, tint: [0.85, 0.68, 0.6] });
  for (const dz of [-L / 2 + 1.2, L / 2 - 1.2]) {
    kit.deco('gunMetal', x, 0.42, z + dz, W - 0.4, 0.5, 1.6, { density: 1.6, tint: [0.7, 0.72, 0.74] });
    for (const dx of [-1.2, 1.2]) {
      kit.cylinder('gunMetal', x + dx, 0.42, z + dz, 0.42, 0.16, { seg: 10, tint: [1.0, 1.02, 1.05] });
    }
  }
  if (fechado) {
    // caixa fechada: vira um bloco alto que corta a linha de tiro do pátio norte
    kit.solid('metalPainted', x, piso + 1.5, z, W, 2.9, L - 0.6, { density: 0.7, tint: [1.0, 0.92, 0.86] });
    for (let dz = -L / 2 + 0.8; dz < L / 2 - 0.6; dz += 0.5) {
      kit.deco('metalPainted', x, piso + 1.5, z + dz, W + 0.08, 2.6, 0.12, { density: 1.6, tint: [0.95, 0.88, 0.82] });
    }
    kit.deco('concrete', x, piso + 3.05, z, W - 0.2, 0.1, L - 0.8, { density: 0.6, tint: [1.55, 1.62, 1.72] });
  } else {
    // gôndola aberta: as laterais dão cobertura de quem sobe nela
    for (const dx of [-W / 2 + 0.1, W / 2 - 0.1]) {
      kit.solid('rust', x + dx, piso + 0.7, z, 0.2, 1.4, L - 0.6, { density: 1.2, tint: [0.9, 0.7, 0.6] });
    }
    for (const dz of [-L / 2 + 0.4, L / 2 - 0.4]) {
      kit.solid('rust', x, piso + 0.7, z + dz, W, 1.4, 0.2, { density: 1.2, tint: [0.9, 0.7, 0.6] });
    }
    kit.deco('sand', x, piso + 0.5, z, W - 0.5, 0.6, L - 1.2, { density: 0.8, tint: [0.5, 0.5, 0.52] });
  }
  // escada de acesso na ponta: sem ela o vagão é só um muro
  kit.stairs('gunMetal', x + W / 2 + 0.1, z - L / 2 - 1.6, 0, 1, 4, 0.29, 0.32, 1.0, 0, { rail: 'right', tint: [1.1, 1.15, 1.2] });
}

// ---------------------------------------------------------------------------
// rede alta: as passarelas que ligam guindaste, pilhas e galpão (rota vertical)
// ---------------------------------------------------------------------------

function passarelas(kit) {
  const y = NIVEL_ALTO;
  // guindaste norte -> topo da fileira de contêineres
  kit.catwalk('metalPainted', -17.0, -15.4, -12.8, -11.2, y, { rails: ['n', 's'], thick: 0.2 });
  kit.catwalk('metalPainted', -15.4, -12.0, -12.8, -11.2, y, { rails: ['n', 's'], thick: 0.2 });
  // ponte sobre o corredor, caindo no topo da pilha de -11.8
  kit.catwalk('metalPainted', -12.0, -9.0, -12.8, -11.2, y, { rails: ['n'], thick: 0.2 });

  // Do topo da fileira de -12.4 para a de -7.2 e daí para leste. As duas cotas
  // abaixo são casadas com a FACE das pilhas, não escolhidas por estética: a
  // ponte norte-sul saía em x[-6,-4], que é vão entre pilhas na fileira de
  // -12.4 — ela terminava no ar, a 5,3 m, a 2,8 m do topo mais próximo. Em
  // x[-8.8,-6.8] ela encosta na face leste da pilha de -11.8 e desemboca na de
  // -8.8. Mesma coisa no lance seguinte: ele parava em x=3.4 e o próximo começa
  // em x=6.0; agora vai até lá.
  kit.catwalk('metalPainted', -8.8, -6.8, -12.0, -6.4, y, { rails: ['w', 'e'], thick: 0.2 });
  kit.catwalk('metalPainted', 0.0, 6.0, -8.6, -7.0, y, { rails: ['n', 's'], thick: 0.2 });
  kit.catwalk('metalPainted', 6.0, 12.6, -8.6, -7.0, y, { rails: ['n', 's'], thick: 0.2 });
  // último lance: entra no galpão pelo vão alto da fachada oeste
  kit.catwalk('metalPainted', 10.4, 13.2, -13.6, -11.0, y, { rails: ['n', 's'], thick: 0.2 });
  kit.catwalk('metalPainted', 9.4, 10.6, -13.6, -7.0, y, { rails: ['w'], thick: 0.2 });
  // e a ligação de dentro do galpão até a passarela em L
  kit.catwalk('metalPainted', 12.6, 13.2, -13.6, -11.0, y, { rails: [], thick: 0.2 });
  kit.catwalk('metalPainted', 12.8, 14.4, -20.4, -11.0, y, { rails: ['w'], thick: 0.2 });

  // --- rede SUL: a plataforma do guindaste de z=10 entrando no labirinto -----
  //
  // Onze pranchas de serviço, cada uma pousando na FACE de duas pilhas de dois
  // níveis (topo 5,2; a prancha fica a 5,3, os 10 cm de sobreposição são de
  // propósito — sem eles sobra vão no ponto exato da chegada). Nenhuma cruza
  // pilha nem perna de guindaste: conferido caixa a caixa contra os colisores
  // do dump antes de escrever. Sem guarda-corpo: em 1,2 m de largura o
  // corrimão, que é COLISOR, fecharia a lateral da chegada.
  //
  // O que cada uma liga está no comentário — é o que permite mover uma pilha
  // depois sem descobrir do jeito difícil que a prancha ficou no ar.
  const pranchas = [
    // [x0, x1, z0, z1]
    [-8.2, -7.0, 7.9, 9.1],        // fileira 9,0 (pilha -11,0) -> atravessada de 6,3
    [-7.0, -5.6, 9.2, 13.3],       // atravessada de 6,3 -> fileira 14,4 (pilha -8,2)
    [-13.6, -12.4, 4.7, 7.9],      // fileira 3,6 (pilha -15,0) -> fileira 9,0 (pilha -11,0)
    [5.0, 6.4, 4.7, 7.9],          // fileira 3,6 (pilha 3,6) -> fileira 9,0 (pilha 7,6)
    [10.4, 12.0, 8.0, 9.2],        // fileira 9,0 (pilha 7,6) -> atravessada de 13,0
    [12.0, 13.4, 8.8, 13.3],       // atravessada de 13,0 -> fileira 14,4 (pilha 10,4)
    [12.2, 13.4, -0.7, 3.1],       // atravessada de 13,0 -> fileira -1,8 (pilha 10,6)
    // e os quatro ramos que faltavam na rede NORTE, que já existia
    [1.0, 2.4, -11.3, -8.5],       // fileira -12,4 (pilha 0,6) -> lance leste-oeste
    [-11.0, -9.6, -6.1, -2.9],     // fileira -7,2 (pilha -8,8) -> fileira -1,8 (pilha -11,4)
    [-14.4, -13.0, -16.5, -13.5],  // fileira -17,6 (pilha -15,4) -> fileira -12,4 (pilha -11,8)
    [-2.2, -0.8, -16.5, -13.5],    // fileira -17,6 (pilha -3,0) -> fileira -12,4 (pilha 0,6)
  ];
  for (const [x0, x1, z0, z1] of pranchas) {
    kit.catwalk('metalPainted', x0, x1, z0, z1, y, { rails: [], thick: 0.2 });
  }

  // cabos de energia atravessando o pátio, todos partindo do poste central
  kit.cable([-11.4, 5.4, -1.0], [-19.6, 5.6, -6.0], 1.2);
  kit.cable([1.4, 6.2, -1.0], [12.6, 6.4, -4.0], 1.6);
  kit.cable([1.0, 6.2, -0.6], [17.6, 5.4, 9.0], 2.0);
  kit.cable([1.0, 6.2, -1.4], [-4.6, 3.0, 27.0], 2.4);
}

// ---------------------------------------------------------------------------
// FASE 2 — espalhamento. Depois de toda a geometria, porque tudo aqui consulta
// groundAt()/isSolid() sobre a lista de colisores já fechada.
// ---------------------------------------------------------------------------

function espalhamento(kit, rng) {
  // parapeitos de sacaria: postos improvisados nos pontos quentes
  kit.emplacement({ x: -22.0, z: -2.0, yaw: Math.PI / 2, len: 3.6, y: 0 });
  kit.emplacement({ x: 15.0, z: 2.0, yaw: 0.2, len: 3.4, y: 0 });
  kit.emplacement({ x: 20.0, z: -8.0, yaw: Math.PI / 2, len: 3.0, y: 0 });
  kit.emplacement({ x: 21.0, z: -8.4, yaw: 0.0, len: 3.2, y: GALPAO.TELHADO });
  kit.emplacement({ x: -2.0, z: -24.0, yaw: 0.35, len: 3.8, y: 0 });
  kit.sandbags();

  kit.crates([
    [-22.4, -20.0], [-21.4, -19.2], [-20.0, 14.0], [-21.0, 14.9], [14.6, -3.0],
    [16.0, 1.0], [24.0, -20.0], [25.0, -19.2], [29.0, -2.0], [13.0, 20.0],
    [-8.0, 24.0], [-7.0, 24.9], [2.0, 26.0], [22.0, 26.0], [30.0, 12.0],
    [-2.0, -30.0], [-9.0, -22.0], [-8.0, -21.0], [11.0, -26.0], [26.0, 6.0],
  ]);

  kit.pallets({
    count: 20,
    area: [-24, 31, -30, 31],
    extra: [[13.6, 0.02, -2.6, 0.2, 1], [13.6, 0.16, -2.6, 0.5, 1], [-22.0, 0.02, 6.0, 1.2, 1]],
  });

  kit.drums([
    [-23.0, -8.0], [-22.3, -8.7], [-23.2, 4.0], [14.8, -20.0], [15.5, -20.7],
    [19.0, -3.0], [28.5, -16.0], [-19.0, 18.0], [-18.2, 18.7], [3.0, -28.0],
    [24.0, 20.0], [30.5, -8.0], [-12.0, 24.0], [7.0, -22.0], [7.8, -22.6],
    [16.0, 14.0], [-24.0, 24.0], [11.0, 10.0],
  ]);

  kit.tyres([[-23.5, 12.0, 4], [-22.2, 12.6, 3], [15.0, 8.0, 3], [28.0, 24.0, 4], [-3.0, 20.0, 2], [21.0, -21.0, 3]]);

  // Entulho concentrado onde a coisa quebrou: rombo do muro norte, portão sul,
  // pé do guindaste e a boca do galpão.
  const focos = [[20.0, -22.0, 3.5], [-5.4, -33.0, 3.0], [-20.5, -12.0, 2.5], [16.6, -6.0, 2.5]];
  kit.rubbleField({ count: 170, hotspots: focos, tint: [1.0, 1.05, 1.12] });
  kit.bricks({ count: 80, hotspots: focos, tint: [0.7, 0.62, 0.6] });
  kit.litter({ count: 90, tint: [1.5, 1.55, 1.6] });

  // Neve empurrada por pá para as bordas: montinhos alongados encostados no
  // que já existe. É o último passo de propósito — usa groundAt para não
  // enterrar meio monte dentro de um contêiner.
  for (let i = 0; i < 22; i++) {
    const x = kit.rand(-24, 31), z = kit.rand(-31, 31);
    if (kit.groundAt(x, z) > 0.2) continue;
    const w = 1.6 + rng() * 3.4;
    kit.deco('concrete', x, 0.22, z, w, 0.44, 0.9 + rng() * 1.4,
      { rotY: rng() * 3.14, density: 0.5, tint: [1.5 + rng() * 0.2, 1.58 + rng() * 0.2, 1.68 + rng() * 0.2] });
  }
}
